import React, { useState, useEffect } from 'react';
import { useBranch } from '../../../layouts/BranchContext';
import BackButton from '../../../ui/BackButton';
import {  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,

} from '@mui/material';
import axios from 'axios';
import {
  Button,
  Container,
  TextField,
  Table,
  Modal,
  TableBody,
  TableCell,
  Box,
  Typography,
  TableContainer,
  IconButton,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  styled,
  Tooltip,
  useTheme,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Chip,
} from '@mui/material';

import {
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  InfoOutlined as InfoOutlineIcon,
  FilterList as FilterListIcon,
  Save as SaveIcon,
  Assignment as AssignmentIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';

import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

import ChevronLeft from '@mui/icons-material/KeyboardDoubleArrowLeft';
import ChevronRight from '@mui/icons-material/KeyboardDoubleArrowRight';
import { PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ReportPdfColorModal from '../../Report/ReportPdfColorModal';
import { usePdfSettings } from '../../../layouts/PdfSettingsContext.js';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/* ============================================================================
   HELPERS
============================================================================ */

const getDateAfterDays = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const getStoredUser = () => {
  const sessionUser = sessionStorage.getItem('user');

  if (sessionUser && sessionUser !== 'undefined') {
    try {
      return JSON.parse(sessionUser);
    } catch (err) {
      console.warn('Invalid user data in sessionStorage:', err);
    }
  }

  const localUser = localStorage.getItem('user');

  if (localUser && localUser !== 'undefined') {
    try {
      return JSON.parse(localUser);
    } catch (err) {
      console.warn('Invalid user data in localStorage:', err);
    }
  }

  return {};
};

const getAuthHeaders = () => {
  const token =
    localStorage.getItem('accessToken') ||
    localStorage.getItem('access_token') ||
    sessionStorage.getItem('accessToken') ||
    sessionStorage.getItem('access_token');

  return {
    Authorization: `Bearer ${token}`,
  };
};

const normalizeApiList = (responseData) => {
  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (Array.isArray(responseData?.results)) {
    return responseData.results;
  }

  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }

  if (Array.isArray(responseData?.items)) {
    return responseData.items;
  }

  return [];
};

// ── Helper: get employee display name ─────────────────────────────────────────
const getEmpName = (emp) =>
  emp?.name ||
  `${emp?.first_name || ''} ${emp?.last_name || ''}`.trim() ||
  emp?.username ||
  `Employee ${emp?.id}`;

// ── Helper: get course name by id ─────────────────────────────────────────────
const getCourseName = (courseId, courseData) => {
  const c = courseData.find(
    (x) => String(x.id) === String(courseId)
  );

  return (
    c?.name ||
    c?.course_name ||
    c?.title ||
    `Course ${courseId}`
  );
};

/* ============================================================================
   CUSTOM SCROLLBAR
============================================================================ */

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.2)'
        : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },

  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.3)'
        : 'rgba(0,0,0,0.3)',
  },

  scrollbarWidth: 'thin',

  scrollbarColor: `${
    theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.2)'
      : 'rgba(0,0,0,0.2)'
  } transparent`,
}));

/* ============================================================================
   ALERT
============================================================================ */

const Alert = React.forwardRef(function Alert(props, ref) {
  return (
    <MuiAlert
      elevation={6}
      ref={ref}
      variant="filled"
      {...props}
    />
  );
});

/* ============================================================================
   COMPONENT
============================================================================ */

const EnquiryFollowUp = () => {
  const theme = useTheme();
  const { pdfThemeColor, pdfTextColor } = usePdfSettings();

  /* ==========================================================================
     GLOBAL BRANCH / ORGANIZATION
  ========================================================================== */

  const { selectedBranchId } = useBranch();

  const storedUser = getStoredUser();

  const organizationId =
    storedUser?.organization_id ||
    storedUser?.organisation_id ||
    storedUser?.organization ||
    storedUser?.organisation ||
    '';

  /* ==========================================================================
     FILTER STATE
  ========================================================================== */

  const [followUpType, setFollowUpType] = useState('');
  const [showDateFields, setShowDateFields] = useState(false);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [isSearchPerformed, setIsSearchPerformed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  /* ==========================================================================
     TABLE DATA
  ========================================================================== */

  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* ==========================================================================
     ASSIGNED TO
  ========================================================================== */

  const [assignedToData, setAssignedToData] = useState([]);

  /* ==========================================================================
     PAGINATION
  ========================================================================== */

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* ==========================================================================
     MODALS
  ========================================================================== */

  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [showPdfColorModal, setShowPdfColorModal] = useState(false);
  const [tempPdfThemeColor, setTempPdfThemeColor] =
    useState(pdfThemeColor);
  const [tempPdfTextColor, setTempPdfTextColor] =
    useState(pdfTextColor);

  /* ==========================================================================
     FOLLOW-UP MODAL DATA
  ========================================================================== */

  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  const [followUpHistory, setFollowUpHistory] = useState([]);

  const [modalLoading, setModalLoading] = useState(false);
const [remarkDialogOpen, setRemarkDialogOpen] = useState(false);
const [selectedRemark, setSelectedRemark] = useState('');
  const [savingFollowUp, setSavingFollowUp] = useState(false);

  /* ==========================================================================
     FOLLOW-UP MASTER DATA
  ========================================================================== */

  const [statusData, setStatusData] = useState([]);
  const [interestLevelData, setInterestLevelData] = useState([]);
  const [followUpMediumData, setFollowUpMediumData] = useState([]);

  /* ==========================================================================
     DEMO CLASS MASTER DATA
     
     Flow:
     Global Branch
         ↓
     Course
         ↓
     Batch
         ↓
     Faculty / Trainer
  ========================================================================== */

  const [courseData, setCourseData] = useState([]);

  const [demoBatches, setDemoBatches] = useState([]);

  const [demoFaculty, setDemoFaculty] = useState([]);

  const [loadingDemoBatches, setLoadingDemoBatches] = useState(false);

  const [loadingDemoFaculty, setLoadingDemoFaculty] = useState(false);

  /* ==========================================================================
     DEMO TEMP STATE
  ========================================================================== */

  const [tempDemoCourse, setTempDemoCourse] = useState('');

  const [tempDemoBatch, setTempDemoBatch] = useState('');

  const [tempDemoFaculty, setTempDemoFaculty] = useState('');

  const [tempDemoDate, setTempDemoDate] = useState('');

  const [tempDemoTime, setTempDemoTime] = useState('');

  /* ==========================================================================
     DEFAULT FOLLOW-UP FORM
  ========================================================================== */

  const defaultFollowUpForm = {
    followup_date: getDateAfterDays(0),

    // ✅ Automatically 10 days after today
    next_followup_date: getDateAfterDays(10),

    preferred_time: '10:00',
    followup_medium: '',
    interest_level: '',
    status: '',
    assigned_to: '',
    decline_reason: '',
    confirmed_for_joining: false,
    remark: '',
    demo_attended: false,
    demo_date: '',
    schedule_demo_class: false,

    demo_course: '',
    demo_batch: '',
    demo_faculty: '',
    demo_time: '',
  };

  const [followUpForm, setFollowUpForm] =
    useState(defaultFollowUpForm);

  /* ==========================================================================
     SNACKBAR
  ========================================================================== */

  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [snackbarMessage, setSnackbarMessage] = useState('');

  const [snackbarSeverity, setSnackbarSeverity] =
    useState('success');

  const showSnack = (
    msg,
    severity = 'success'
  ) => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  /* ==========================================================================
     FIELD STYLE
  ========================================================================== */

  const fieldSx = {
    '& .MuiInputLabel-root': {
      color: theme.palette.text.primary,
    },

    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.primary,
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },

    '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator':
      {
        filter:
          theme.palette.mode === 'dark'
            ? 'invert(1)'
            : 'none',
      },

    '& .MuiInputBase-input[type="time"]::-webkit-calendar-picker-indicator':
      {
        filter:
          theme.palette.mode === 'dark'
            ? 'invert(1)'
            : 'none',
      },
  };

  /* ==========================================================================
     FETCH BRANCH-SPECIFIC COURSES
     
     GET:
     /api/course/courses/?organization=<id>&branch=<id>
  ========================================================================== */

  const fetchBranchCourses = async () => {
    if (!selectedBranchId) {
      setCourseData([]);

      setTempDemoCourse('');
      setTempDemoBatch('');
      setTempDemoFaculty('');

      setDemoBatches([]);
      setDemoFaculty([]);

      return;
    }

    try {
      const headers = getAuthHeaders();

      const params = {
        branch: selectedBranchId,
      };

      if (organizationId) {
        params.organization = organizationId;
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/course/courses/`,
        {
          headers,
          params,
        }
      );

      const courses = normalizeApiList(
        response.data
      );

      setCourseData(courses);

      // Reset dependent selections when branch changes
      setTempDemoCourse('');
      setTempDemoBatch('');
      setTempDemoFaculty('');

      setDemoBatches([]);
      setDemoFaculty([]);
    } catch (err) {
      console.error(
        'Error fetching branch-specific courses:',
        err
      );

      setCourseData([]);

      setTempDemoCourse('');
      setTempDemoBatch('');
      setTempDemoFaculty('');

      setDemoBatches([]);
      setDemoFaculty([]);

      showSnack(
        'Failed to load courses for selected branch.',
        'error'
      );
    }
  };

  /* ==========================================================================
     FETCH BATCHES FOR SELECTED COURSE + GLOBAL BRANCH
     
     GET:
     /api/course/batches/?organization=<id>&branch=<id>
     
     Then filter returned batches by selected course.
  ========================================================================== */

  const fetchDemoBatches = async (
    courseId,
    preserveBatchId = '',
    preserveFacultyId = ''
  ) => {
    if (!courseId || !selectedBranchId) {
      setDemoBatches([]);

      setTempDemoBatch('');

      setDemoFaculty([]);

      setTempDemoFaculty('');

      return;
    }

    setLoadingDemoBatches(true);

    // Clear dependent fields first
    setDemoBatches([]);

    if (!preserveBatchId) {
      setTempDemoBatch('');
      setTempDemoFaculty('');
    }

    setDemoFaculty([]);

    if (!preserveFacultyId) {
      setTempDemoFaculty('');
    }

    try {
      const headers = getAuthHeaders();

      const params = {
        branch: selectedBranchId,
      };

      if (organizationId) {
        params.organization = organizationId;
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/course/batches/`,
        {
          headers,
          params,
        }
      );

      const allBatches = normalizeApiList(
        response.data
      );

      // --------------------------------------------------------------
      // Only batches belonging to selected course
      // --------------------------------------------------------------

      const courseBatches = allBatches.filter(
        (batch) =>
          String(
            batch.course ??
              batch.course_id ??
              batch.courseId
          ) === String(courseId)
      );

      setDemoBatches(courseBatches);

      // --------------------------------------------------------------
      // Preserve existing batch when EDITING demo
      // --------------------------------------------------------------

      if (preserveBatchId) {
        const existingBatch =
          courseBatches.find(
            (batch) =>
              String(batch.id) ===
              String(preserveBatchId)
          );

        if (existingBatch) {
          setTempDemoBatch(
            String(existingBatch.id)
          );

          const trainerId =
            existingBatch.trainer ??
            existingBatch.trainer_id ??
            existingBatch.faculty ??
            existingBatch.faculty_id ??
            existingBatch.teacher ??
            existingBatch.teacher_id;

          const trainerName =
            existingBatch.trainer_name ??
            existingBatch.faculty_name ??
            existingBatch.teacher_name ??
            '';

          if (trainerId) {
            setDemoFaculty([
              {
                id: trainerId,
                name:
                  trainerName ||
                  `Trainer ${trainerId}`,
              },
            ]);

            setTempDemoFaculty(
              preserveFacultyId
                ? String(preserveFacultyId)
                : String(trainerId)
            );
          } else {
            setDemoFaculty([]);
            setTempDemoFaculty('');
          }

          return;
        }
      }

      // --------------------------------------------------------------
      // Auto-select when only one batch exists
      // --------------------------------------------------------------

      if (courseBatches.length === 1) {
        const onlyBatch = courseBatches[0];

        setTempDemoBatch(
          String(onlyBatch.id)
        );

        const trainerId =
          onlyBatch.trainer ??
          onlyBatch.trainer_id ??
          onlyBatch.faculty ??
          onlyBatch.faculty_id ??
          onlyBatch.teacher ??
          onlyBatch.teacher_id;

        const trainerName =
          onlyBatch.trainer_name ??
          onlyBatch.faculty_name ??
          onlyBatch.teacher_name ??
          '';

        if (trainerId) {
          setDemoFaculty([
            {
              id: trainerId,
              name:
                trainerName ||
                `Trainer ${trainerId}`,
            },
          ]);

          setTempDemoFaculty(
            String(trainerId)
          );
        }
      }
    } catch (err) {
      console.error(
        'Error fetching demo batches:',
        err
      );

      setDemoBatches([]);

      setTempDemoBatch('');

      setDemoFaculty([]);

      setTempDemoFaculty('');

      showSnack(
        'Failed to load batches for selected course.',
        'error'
      );
    } finally {
      setLoadingDemoBatches(false);
    }
  };

  /* ==========================================================================
     SET FACULTY FROM SELECTED BATCH
  ========================================================================== */

  const setFacultyFromBatch = (
    batchId,
    batches = demoBatches
  ) => {
    if (!batchId) {
      setDemoFaculty([]);

      setTempDemoFaculty('');

      return;
    }

    setLoadingDemoFaculty(true);

    try {
      const selectedBatch =
        batches.find(
          (batch) =>
            String(batch.id) ===
            String(batchId)
        );

      if (!selectedBatch) {
        setDemoFaculty([]);

        setTempDemoFaculty('');

        return;
      }

      const trainerId =
        selectedBatch.trainer ??
        selectedBatch.trainer_id ??
        selectedBatch.faculty ??
        selectedBatch.faculty_id ??
        selectedBatch.teacher ??
        selectedBatch.teacher_id;

      const trainerName =
        selectedBatch.trainer_name ??
        selectedBatch.faculty_name ??
        selectedBatch.teacher_name ??
        '';

      if (trainerId) {
        const faculty = {
          id: trainerId,
          name:
            trainerName ||
            `Trainer ${trainerId}`,
        };

        setDemoFaculty([faculty]);

        // Faculty is fixed according to selected batch
        setTempDemoFaculty(
          String(trainerId)
        );
      } else {
        setDemoFaculty([]);

        setTempDemoFaculty('');
      }
    } catch (err) {
      console.error(
        'Error setting faculty from batch:',
        err
      );

      setDemoFaculty([]);

      setTempDemoFaculty('');
    } finally {
      setLoadingDemoFaculty(false);
    }
  };

  /* ==========================================================================
     MASTER DATA
  ========================================================================== */

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const headers = getAuthHeaders();

        const [
          statusRes,
          interestRes,
          mediumRes,
          assignedRes,
        ] = await Promise.all([
          axios.get(
            `${API_BASE_URL}/api/master/status/`,
            { headers }
          ),

          axios.get(
            `${API_BASE_URL}/api/master/interest-levels/`,
            { headers }
          ),

          axios.get(
            `${API_BASE_URL}/api/master/followup-mediums/`,
            { headers }
          ),

          axios.get(
            `${API_BASE_URL}/api/staff/employees/`,
            { headers }
          ),
        ]);

        /* --------------------------------------------------------------
           Status
        -------------------------------------------------------------- */

        setStatusData(
          normalizeApiList(statusRes.data)
        );

        /* --------------------------------------------------------------
           Interest Levels
        -------------------------------------------------------------- */

        setInterestLevelData(
          normalizeApiList(interestRes.data)
        );

        /* --------------------------------------------------------------
           Follow-Up Medium
        -------------------------------------------------------------- */

        setFollowUpMediumData(
          normalizeApiList(mediumRes.data)
        );

        /* --------------------------------------------------------------
           Assigned To
        -------------------------------------------------------------- */

        const empList =
          normalizeApiList(
            assignedRes.data
          );

        const counsellors =
          empList.filter(
            (employee) =>
              employee.role
                ?.toLowerCase() ===
                'counsellor' ||
              employee.designation_name
                ?.toLowerCase() ===
                'counsellor' ||
              employee.designation_name
                ?.toLowerCase() ===
                'counselor' ||
              employee.user_type
                ?.toLowerCase() ===
                'counsellor'
          );

        setAssignedToData(
          counsellors
        );
      } catch (err) {
        console.error(
          'Error fetching master data:',
          err
        );
      }
    };

    fetchMasterData();
  }, []);

  /* ==========================================================================
     LOAD COURSES WHEN GLOBAL BRANCH CHANGES
  ========================================================================== */

  useEffect(() => {
    fetchBranchCourses();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchId, organizationId]);

  /* ==========================================================================
     DATE FILTER AUTO RANGE
  ========================================================================== */

  useEffect(() => {
    if (showDateFields) {
      const today = new Date();

      const oneMonthAgo =
        new Date();

      oneMonthAgo.setMonth(
        today.getMonth() - 1
      );

      const formatDate = (date) => {
        const year =
          date.getFullYear();

        const month = String(
          date.getMonth() + 1
        ).padStart(2, '0');

        const day = String(
          date.getDate()
        ).padStart(2, '0');

        return `${year}-${month}-${day}`;
      };

      setToDate(
        formatDate(today)
      );

      setFromDate(
        formatDate(oneMonthAgo)
      );
    }
  }, [showDateFields]);

  /* ==========================================================================
     AUTO SEARCH WHEN FILTER TYPE CHANGES
  ========================================================================== */

  useEffect(() => {
    if (
      followUpType &&
      followUpType !== 'SelectDate'
    ) {
      handleSearch();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followUpType]);

  /* ==========================================================================
     RE-SEARCH WHEN BRANCH CHANGES
  ========================================================================== */

  useEffect(() => {
    if (
      isSearchPerformed &&
      followUpType &&
      followUpType !== 'SelectDate'
    ) {
      handleSearch();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchId]);

  /* ==========================================================================
     CLIENT SIDE SEARCH FILTER
  ========================================================================== */

  useEffect(() => {
    const term =
      searchTerm.toLowerCase();

    const filtered =
      enquiries.filter(
        (enquiry) =>
          (
            enquiry.enquiry_code ||
            ''
          )
            .toLowerCase()
            .includes(term) ||
          (
            enquiry.candidate_name ||
            ''
          )
            .toLowerCase()
            .includes(term) ||
          (
            enquiry.mobile_no ||
            ''
          )
            .toLowerCase()
            .includes(term)
      );

    setFilteredEnquiries(
      filtered
    );

    setPage(1);
  }, [
    searchTerm,
    enquiries,
  ]);

  /* ==========================================================================
     SEARCH / FETCH ENQUIRIES
  ========================================================================== */

  const handleSearch = async () => {
    if (
      followUpType === 'SelectDate' &&
      (!fromDate || !toDate)
    ) {
      setError(
        'Please select both From Date and To Date.'
      );

      return;
    }

    setIsSearchPerformed(true);

    setLoading(true);

    setError(null);

    try {
      const headers =
        getAuthHeaders();

      const todayStr =
        getDateAfterDays(0);

      let params = {};

      /* --------------------------------------------------------------
         Date Wise
      -------------------------------------------------------------- */

      if (
        followUpType ===
        'SelectDate'
      ) {
        params = {
          next_followup_from:
            fromDate,
          next_followup_to:
            toDate,
        };
      }

      /* --------------------------------------------------------------
         Current Date
      -------------------------------------------------------------- */

      else if (
        followUpType ===
        'AllCurrentDate'
      ) {
        params = {
          single_date:
            todayStr,
        };
      }

      /* --------------------------------------------------------------
         Pending
      -------------------------------------------------------------- */

      else if (
        followUpType ===
        'PendingFollowUp'
      ) {
        params = {};
      }

      /* --------------------------------------------------------------
         Global Branch
      -------------------------------------------------------------- */

      if (selectedBranchId) {
        params.branch =
          selectedBranchId;
      }

      /* --------------------------------------------------------------
         Organization
      -------------------------------------------------------------- */

      if (organizationId) {
        params.organisation =
          organizationId;
      }

      const enquiriesRes =
        await axios.get(
          `${API_BASE_URL}/api/student_details/enquiries/`,
          {
            headers,
            params,
          }
        );

      let data =
        enquiriesRes.data || [];

      if (!Array.isArray(data)) {
        data =
          data.results ||
          data.data ||
          [];
      }

      /* --------------------------------------------------------------
         Pending filter
      -------------------------------------------------------------- */

      if (
        followUpType ===
        'PendingFollowUp'
      ) {
        data = data.filter(
          (enquiry) =>
            enquiry.next_followup_date &&
            enquiry.next_followup_date <
              todayStr
        );
      }

      /* --------------------------------------------------------------
         Current Date
      -------------------------------------------------------------- */

      else if (
        followUpType ===
        'AllCurrentDate'
      ) {
        data = data.filter(
          (enquiry) =>
            enquiry.next_followup_date ===
            todayStr
        );
      }

      /* --------------------------------------------------------------
         Selected Date
      -------------------------------------------------------------- */

      else if (
        followUpType ===
        'SelectDate'
      ) {
        data = data.filter(
          (enquiry) =>
            enquiry.next_followup_date &&
            enquiry.next_followup_date >=
              fromDate &&
            enquiry.next_followup_date <=
              toDate
        );
      }

      setEnquiries(data);

      setFilteredEnquiries(data);

      if (data.length === 0) {
        setError(
          'No enquiries found for the selected criteria.'
        );
      }
    } catch (err) {
      console.error(
        'Error fetching enquiries:',
        err
      );

      setError(
        'Error fetching data. Please try again.'
      );

      setEnquiries([]);

      setFilteredEnquiries([]);
    } finally {
      setLoading(false);
    }
  };

  /* ==========================================================================
     FOLLOW-UP TYPE CHANGE
  ========================================================================== */

  const handleFollowUpTypeChange = (
    e
  ) => {
    const value =
      e.target.value;

    setFollowUpType(value);

    setShowDateFields(
      value === 'SelectDate'
    );

    setEnquiries([]);

    setFilteredEnquiries([]);

    setError(null);

    setIsSearchPerformed(false);

    setLoading(false);
  };

  /* ==========================================================================
     RESET
  ========================================================================== */

  const handleReset = () => {
    setFollowUpType('');

    setShowDateFields(false);

    setFromDate('');

    setToDate('');

    setEnquiries([]);

    setFilteredEnquiries([]);

    setError(null);

    setSearchTerm('');

    setPage(1);

    setIsSearchPerformed(false);

    setLoading(false);
  };

  /* ==========================================================================
     TABLE ASSIGNED TO
  ========================================================================== */

  const handleTableAssignedToChange = async (
    enquiry,
    newAssignedTo
  ) => {
    try {
      const headers =
        getAuthHeaders();

      await axios.put(
        `${API_BASE_URL}/api/student_details/enquiries/${enquiry.id}/`,
        {
          assigned_to:
            parseInt(
              newAssignedTo,
              10
            ),
        },
        {
          headers,
        }
      );

      setEnquiries(
        (previous) =>
          previous.map(
            (item) =>
              item.id === enquiry.id
                ? {
                    ...item,
                    assigned_to:
                      parseInt(
                        newAssignedTo,
                        10
                      ),
                  }
                : item
          )
      );

      showSnack(
        'Assigned To updated successfully!'
      );
    } catch (err) {
      console.error(
        'Error updating assigned_to:',
        err
      );

      showSnack(
        'Failed to update Assigned To.',
        'error'
      );
    }
  };

  /* ==========================================================================
     OPEN FOLLOW-UP MODAL
  ========================================================================== */

  const handleOpenFollowUpModal =
    async (enquiry) => {
      setModalLoading(true);

      setShowFollowUpModal(true);

      // ✅ New form automatically gets next date +10 days
      setFollowUpForm({
        ...defaultFollowUpForm,
      });

      setSelectedEnquiry(null);

      setFollowUpHistory([]);

      try {
        const headers =
          getAuthHeaders();

        /* ------------------------------------------------------------
           1. Enquiry Detail
        ------------------------------------------------------------ */

        const detailRes =
          await axios.get(
            `${API_BASE_URL}/api/student_details/enquiry/followup/details/${enquiry.id}/`,
            {
              headers,
            }
          );

        const detail =
          detailRes.data?.result ||
          detailRes.data;

        setSelectedEnquiry(
          detail
        );

        /* ------------------------------------------------------------
           Prefill Assigned To
        ------------------------------------------------------------ */

        setFollowUpForm(
          (previous) => ({
            ...previous,

            assigned_to:
              detail?.assigned_to
                ? String(
                    detail.assigned_to
                  )
                : '',
          })
        );

        /* ------------------------------------------------------------
           2. Follow-Up History
        ------------------------------------------------------------ */

        const historyRes =
          await axios.get(
            `${API_BASE_URL}/api/student_details/enquiry/followups/`,
            {
              headers,

              params: {
                enquiry:
                  enquiry.id,

                organisation:
                  organizationId,

                branch:
                  selectedBranchId,
              },
            }
          );

        const historyData =
          historyRes.data?.results ||
          historyRes.data?.data ||
          historyRes.data ||
          [];

        setFollowUpHistory(
          Array.isArray(
            historyData
          )
            ? historyData
            : []
        );
      } catch (err) {
        console.error(
          'Error loading follow-up details:',
          err
        );

        setSelectedEnquiry({
          enquiry: enquiry.id,

          enquiry_code:
            enquiry.enquiry_code,

          client_name:
            enquiry.candidate_name,

          email:
            enquiry.email,

          mobile_no:
            enquiry.mobile_no,

          address:
            enquiry.address,

          assigned_to:
            enquiry.assigned_to,

          assigned_to_name:
            enquiry.assigned_to_name,
        });

        setFollowUpForm(
          (previous) => ({
            ...previous,

            assigned_to:
              enquiry.assigned_to
                ? String(
                    enquiry.assigned_to
                  )
                : '',
          })
        );

        showSnack(
          'Could not load follow-up details. Showing available data.',
          'warning'
        );
      } finally {
        setModalLoading(false);
      }
    };

  /* ==========================================================================
     FOLLOW-UP FORM CHANGE
  ========================================================================== */

  const handleFollowUpFormChange = (
    e
  ) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setFollowUpForm(
      (previous) => {
        const updated = {
          ...previous,

          [name]:
            type === 'checkbox'
              ? checked
              : value,
        };

        /* ----------------------------------------------------------
           Demo Attended unchecked
        ---------------------------------------------------------- */

        if (
          name ===
            'demo_attended' &&
          !checked
        ) {
          updated.demo_date = '';
        }

        /* ----------------------------------------------------------
           Schedule Demo unchecked
        ---------------------------------------------------------- */

        if (
          name ===
            'schedule_demo_class' &&
          !checked
        ) {
          updated.demo_course = '';
          updated.demo_batch = '';
          updated.demo_faculty = '';
          updated.demo_time = '';

          setTempDemoCourse('');
          setTempDemoBatch('');
          setTempDemoFaculty('');
          setTempDemoDate('');
          setTempDemoTime('');

          setDemoBatches([]);
          setDemoFaculty([]);
        }

        return updated;
      }
    );

    /* --------------------------------------------------------------
       Open Demo Modal
    -------------------------------------------------------------- */

    if (
      name ===
        'schedule_demo_class' &&
      checked
    ) {
      const existingCourse =
        followUpForm.demo_course ||
        '';

      const existingBatch =
        followUpForm.demo_batch ||
        '';

      const existingFaculty =
        followUpForm.demo_faculty ||
        '';

      const existingDate =
        followUpForm.demo_date ||
        getDateAfterDays(0);

      const existingTime =
        followUpForm.demo_time ||
        '';

      setTempDemoCourse(
        existingCourse
      );

      setTempDemoBatch(
        existingBatch
      );

      setTempDemoFaculty(
        existingFaculty
      );

      setTempDemoDate(
        existingDate
      );

      setTempDemoTime(
        existingTime
      );

      setShowDemoModal(true);

      // ------------------------------------------------------------
      // When editing existing demo, load its branch-specific
      // batches and preserve the existing batch/faculty.
      // ------------------------------------------------------------

      if (existingCourse) {
        fetchDemoBatches(
          existingCourse,
          existingBatch,
          existingFaculty
        );
      }
    }
  };

  /* ==========================================================================
     COURSE CHANGE
     
     Course selected:
         Global Branch
              ↓
           Course
              ↓
           Batches
  ========================================================================== */

  const handleDemoCourseChange = (
    courseId
  ) => {
    setTempDemoCourse(
      courseId
    );

    // Clear dependent selections
    setTempDemoBatch('');

    setTempDemoFaculty('');

    setDemoFaculty([]);

    if (!courseId) {
      setDemoBatches([]);

      return;
    }

    fetchDemoBatches(
      courseId
    );
  };

  /* ==========================================================================
     BATCH CHANGE
     
     Batch selected:
         Batch
           ↓
       Faculty
  ========================================================================== */

  const handleDemoBatchChange = (
    batchId
  ) => {
    setTempDemoBatch(
      batchId
    );

    setTempDemoFaculty('');

    setFacultyFromBatch(
      batchId
    );
  };

  /* ==========================================================================
     SAVE DEMO DETAILS
  ========================================================================== */

  const handleSaveDemoDetails = () => {
    if (
      !tempDemoCourse ||
      !tempDemoBatch ||
      !tempDemoFaculty ||
      !tempDemoDate ||
      !tempDemoTime
    ) {
      showSnack(
        'Please complete all demo class details.',
        'error'
      );

      return;
    }

    setFollowUpForm(
      (previous) => ({
        ...previous,

        demo_course:
          tempDemoCourse,

        demo_batch:
          tempDemoBatch,

        demo_faculty:
          tempDemoFaculty,

        demo_date:
          tempDemoDate,

        demo_time:
          tempDemoTime,
      })
    );

    setShowDemoModal(
      false
    );
  };

  /* ==========================================================================
     SAVE FOLLOW-UP
  ========================================================================== */

  const handleSaveFollowUp =
    async () => {
      if (!selectedEnquiry) {
        return;
      }

      /* --------------------------------------------------------------
         Validation
      -------------------------------------------------------------- */

      const requiredFields = [
        ['Follow-Up Date', followUpForm.followup_date],
        ['Next Follow-Up Date', followUpForm.next_followup_date],
        ['Preferred Time', followUpForm.preferred_time],
        ['Tracked Through', followUpForm.followup_medium],
        ['Interest Level', followUpForm.interest_level],
        ['Reason For Decline/Delay', followUpForm.decline_reason],
        ['Assigned To', followUpForm.assigned_to],
        ['Remark', followUpForm.remark],
      ];

      const missingField = requiredFields.find(
        ([, value]) => !String(value ?? '').trim()
      );

      if (missingField) {
        showSnack(`${missingField[0]} is required.`, 'error');
        return;
      }

      if (
        !followUpForm.remark
      ) {
        showSnack(
          'Remark is required.',
          'error'
        );

        return;
      }

      if (
        followUpForm.schedule_demo_class
      ) {
        if (
          !followUpForm.demo_course ||
          !followUpForm.demo_batch ||
          !followUpForm.demo_faculty ||
          !followUpForm.demo_date ||
          !followUpForm.demo_time
        ) {
          showSnack(
            'Please complete all demo class details.',
            'error'
          );

          return;
        }
      }

      setSavingFollowUp(
        true
      );

      try {
        const headers =
          getAuthHeaders();

        const payload = {
          enquiry:
            selectedEnquiry.enquiry ||
            selectedEnquiry.id,

          followup_date:
            followUpForm.followup_date,

          next_followup_date:
            followUpForm.next_followup_date ||
            null,

          preferred_time:
            followUpForm.preferred_time ||
            null,

          followup_medium:
            followUpForm.followup_medium
              ? parseInt(
                  followUpForm.followup_medium,
                  10
                )
              : null,

          interest_level:
            followUpForm.interest_level
              ? parseInt(
                  followUpForm.interest_level,
                  10
                )
              : null,

          status:
            followUpForm.status
              ? parseInt(
                  followUpForm.status,
                  10
                )
              : null,

          assigned_to:
            followUpForm.assigned_to
              ? parseInt(
                  followUpForm.assigned_to,
                  10
                )
              : null,

          decline_reason:
            followUpForm.decline_reason ||
            null,

          confirmed_for_joining:
            followUpForm.confirmed_for_joining,

          remark:
            followUpForm.remark,

          demo_attended:
            followUpForm.demo_attended,

          demo_date:
            followUpForm.demo_attended
              ? followUpForm.demo_date ||
                null
              : null,

          schedule_demo_class:
            followUpForm.schedule_demo_class,

          demo_course:
            followUpForm.schedule_demo_class &&
            followUpForm.demo_course
              ? parseInt(
                  followUpForm.demo_course,
                  10
                )
              : null,

          demo_batch:
            followUpForm.schedule_demo_class &&
            followUpForm.demo_batch
              ? parseInt(
                  followUpForm.demo_batch,
                  10
                )
              : null,

          demo_faculty:
            followUpForm.schedule_demo_class &&
            followUpForm.demo_faculty
              ? parseInt(
                  followUpForm.demo_faculty,
                  10
                )
              : null,

          demo_time:
            followUpForm.schedule_demo_class
              ? followUpForm.demo_time ||
                null
              : null,
        };

        /* --------------------------------------------------------------
           Remove null / undefined
        -------------------------------------------------------------- */

        Object.keys(
          payload
        ).forEach((key) => {
          if (
            payload[key] === null ||
            payload[key] === undefined
          ) {
            delete payload[key];
          }
        });

        /* --------------------------------------------------------------
           SAVE
        -------------------------------------------------------------- */

        await axios.post(
          `${API_BASE_URL}/api/student_details/enquiry/followup/save/?organisation=${organizationId}&branch=${selectedBranchId}`,
          payload,
          {
            headers,
          }
        );

        showSnack(
          'Follow-up saved successfully!'
        );

        /* --------------------------------------------------------------
           Refresh History
        -------------------------------------------------------------- */

        const historyRes =
          await axios.get(
            `${API_BASE_URL}/api/student_details/enquiry/followups/`,
            {
              headers,

              params: {
                enquiry:
                  selectedEnquiry.enquiry ||
                  selectedEnquiry.id,

                organisation:
                  organizationId,

                branch:
                  selectedBranchId,
              },
            }
          );

        const historyData =
          historyRes.data?.results ||
          historyRes.data?.data ||
          historyRes.data ||
          [];

        setFollowUpHistory(
          Array.isArray(
            historyData
          )
            ? historyData
            : []
        );

        // ✅ Reset to fresh form
        // Next Follow-Up Date again becomes today + 10 days
        setFollowUpForm({
          ...defaultFollowUpForm,
        });

        setTempDemoCourse('');
        setTempDemoBatch('');
        setTempDemoFaculty('');
        setTempDemoDate('');
        setTempDemoTime('');

        setDemoBatches([]);
        setDemoFaculty([]);
      } catch (err) {
        console.error(
          'Save follow-up error:',
          err
        );

        const msg =
          err.response?.data
            ?.detail ||
          err.response?.data
            ?.error ||
          'Failed to save follow-up.';

        showSnack(
          msg,
          'error'
        );
      } finally {
        setSavingFollowUp(
          false
        );
      }
    };

  /* ==========================================================================
     PAGINATION
  ========================================================================== */

  const paginatedData =
    filteredEnquiries.slice(
      (page - 1) *
        rowsPerPage,

      page *
        rowsPerPage
    );

  const totalPages =
    Math.ceil(
      filteredEnquiries.length /
        rowsPerPage
    );

  useEffect(() => {
    if (showPdfColorModal) {
      setTempPdfThemeColor(pdfThemeColor);
      setTempPdfTextColor(pdfTextColor);
    }
  }, [showPdfColorModal, pdfThemeColor, pdfTextColor]);

  const hexToRgb = (hexColor) => {
    const color = hexColor.replace('#', '');
    return [
      parseInt(color.slice(0, 2), 16),
      parseInt(color.slice(2, 4), 16),
      parseInt(color.slice(4, 6), 16),
    ];
  };

  const getEnquiryExportRows = () =>
    filteredEnquiries.map((enquiry, index) => {
      const status =
        enquiry.status_name ||
        statusData.find(
          (item) => String(item.id) === String(enquiry.status)
        )?.name ||
        statusData.find(
          (item) => String(item.id) === String(enquiry.status)
        )?.status_name ||
        'N/A';
      const assignedTo =
        assignedToData.find(
          (employee) => String(employee.id) === String(enquiry.assigned_to)
        );
      const courses =
        enquiry.courses_details
          ?.map((course) => course.course_name || course.name)
          .filter(Boolean)
          .join(', ') || 'N/A';

      return [
        String(index + 1),
        enquiry.enquiry_code || 'N/A',
        enquiry.candidate_name || 'N/A',
        enquiry.mobile_no || 'N/A',
        enquiry.enquiry_date || 'N/A',
        enquiry.next_followup_date || 'N/A',
        courses,
        status,
        assignedTo ? getEmpName(assignedTo) : 'N/A',
      ];
    });

  const generateEnquiryPdf = (
    backgroundColor = pdfThemeColor,
    textColor = pdfTextColor,
    shouldPrint = false
  ) => {
    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 10;
      const backgroundRgb = hexToRgb(backgroundColor);
      const textRgb = hexToRgb(textColor);

      doc.setFillColor(...backgroundRgb);
      doc.rect(0, 0, pageWidth, 22, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text('Enquiry Follow Up', pageWidth / 2, 10, {
        align: 'center',
      });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(
        `${filteredEnquiries.length} record(s)`,
        pageWidth / 2,
        17,
        { align: 'center' }
      );

      autoTable(doc, {
        startY: 27,
        head: [[
          'S.No',
          'Enquiry Code',
          'Client Name',
          'Mobile No',
          'Enquiry Date',
          'Next Follow-Up Date',
          'Product',
          'Status',
          'Assigned To',
        ]],
        body: getEnquiryExportRows(),
        theme: 'grid',
        margin: { left: margin, right: margin },
        styles: {
          font: 'helvetica',
          fontSize: 8,
          textColor: textRgb,
          lineColor: [226, 232, 240],
          cellPadding: 2,
        },
        headStyles: {
          fillColor: backgroundRgb,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252],
        },
        didParseCell: (data) => {
          if (data.section === 'body') {
            data.cell.styles.textColor = textRgb;
          }
        },
      });

      if (shouldPrint) {
        doc.autoPrint();
      }

      const pdfUrl = doc.output('bloburl');
      const pdfWindow = window.open(pdfUrl, '_blank');
      if (!pdfWindow) {
        showSnack(
          'The browser blocked the PDF window. Allow pop-ups and try again.',
          'error'
        );
        return;
      }
      setShowPdfColorModal(false);
    } catch (exportError) {
      console.error('Enquiry PDF export error:', exportError);
      showSnack('Failed to generate the enquiry PDF.', 'error');
    }
  };

  /* ==========================================================================
     RENDER
  ========================================================================== */

  return (
    <Container
      maxWidth={false}
      sx={{
        maxWidth: '1400px',
        mx: 'auto',
        pb: 4,
      }}
    >
      <BackButton />

      <ReportPdfColorModal
        open={showPdfColorModal}
        onClose={() => setShowPdfColorModal(false)}
        themeColor={tempPdfThemeColor}
        setThemeColor={setTempPdfThemeColor}
        textColor={tempPdfTextColor}
        setTextColor={setTempPdfTextColor}
        reportTitle="Enquiry Follow Up"
        onGenerate={(backgroundColor, textColor) =>
          generateEnquiryPdf(backgroundColor, textColor)
        }
        onPrint={(backgroundColor, textColor) =>
          generateEnquiryPdf(backgroundColor, textColor, true)
        }
        loading={loading}
        disabled={!filteredEnquiries.length}
      />

      {/* ======================================================================
          FILTER ROW
      ====================================================================== */}

      <Paper
        elevation={3}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          backgroundColor:
            theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <FormControl
            sx={{
              minWidth: 220,
              ...fieldSx,
            }}
          >
            <InputLabel id="follow-up-type-label">
              Follow Up Type
            </InputLabel>

            <Select
              labelId="follow-up-type-label"
              value={
                followUpType
              }
              onChange={
                handleFollowUpTypeChange
              }
              label="Follow Up Type"
            >
              <MenuItem value="">
                Select Follow Up Type
              </MenuItem>

              <MenuItem value="SelectDate">
                Selected Date Wise
              </MenuItem>

              <MenuItem value="AllCurrentDate">
                Current Date
              </MenuItem>

              <MenuItem value="PendingFollowUp">
                Pending
              </MenuItem>
            </Select>
          </FormControl>

          {showDateFields && (
            <>
              <TextField
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(e) =>
                  setFromDate(
                    e.target.value
                  )
                }
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  maxWidth: 200,
                  ...fieldSx,
                }}
              />

              <TextField
                label="To Date"
                type="date"
                value={toDate}
                onChange={(e) =>
                  setToDate(
                    e.target.value
                  )
                }
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  maxWidth: 200,
                  ...fieldSx,
                }}
              />

              <Button
                variant="contained"
                onClick={
                  handleSearch
                }
                sx={{
                  height: 56,
                  px: 4,
                  fontWeight:
                    'bold',
                  borderRadius:
                    '8px',
                }}
              >
                Search
              </Button>
            </>
          )}

          {followUpType && (
            <Button
              variant="outlined"
              color="primary"
              onClick={
                handleReset
              }
              sx={{
                height: 56,
                px: 3,
                borderRadius:
                  '8px',
              }}
            >
              Reset
            </Button>
          )}

          <Tooltip
            title="View page guide"
            arrow
            placement="right"
          >
            <IconButton
              onClick={() =>
                setShowUserGuideModal(
                  true
                )
              }
              size="small"
              sx={{
                color:
                  'text.disabled',
                backgroundColor:
                  'text.secondary',
                borderRadius:
                  '50%',
                width: 32,
                height: 32,

                '&:hover': {
                  backgroundColor:
                    'rgba(255,255,255,0.2)',
                  color: '#fff',
                  transform:
                    'scale(1.1)',
                },
              }}
            >
              <InfoOutlineIcon
                sx={{
                  fontSize: 18,
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* ======================================================================
          ERROR
      ====================================================================== */}

      {error && (
        <Typography
          color="error"
          sx={{
            mb: 2,
            px: 1,
          }}
        >
          {error}
        </Typography>
      )}

      {/* ======================================================================
          LOADING
      ====================================================================== */}

      {loading && (
        <Typography
          variant="body1"
          sx={{
            mb: 2,
            px: 1,
          }}
        >
          Loading...
        </Typography>
      )}

      {/* ======================================================================
          MAIN TABLE
      ====================================================================== */}

      {!loading &&
        enquiries.length > 0 && (
          <Paper
            elevation={3}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              backgroundColor:
                theme.palette.background.paper,
            }}
          >
            {/* Search + count */}

            <Box
              sx={{
                p: 2,
                display: 'flex',
                alignItems:
                  'center',
                justifyContent:
                  'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <TextField
                variant="outlined"
                placeholder="Search by name, code or mobile..."
                value={searchTerm}
                onChange={(e) =>
                  setSearchTerm(
                    e.target.value
                  )
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{
                          color:
                            theme.palette
                              .primary
                              .main,
                        }}
                      />
                    </InputAdornment>
                  ),

                  endAdornment:
                    searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setSearchTerm(
                              ''
                            )
                          }
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),

                  sx: {
                    borderRadius:
                      '30px',

                    backgroundColor:
                      theme.palette
                        .mode ===
                      'dark'
                        ? 'rgba(255,255,255,0.1)'
                        : '#fff',

                    height: '48px',
                  },
                }}
                sx={{
                  width: 340,
                }}
              />

              <Typography
                variant="body2"
                color="text.disabled"
              >
                {
                  filteredEnquiries.length
                }{' '}
                record(s) found
              </Typography>

              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => setShowPdfColorModal(true)}
                sx={{
                  borderRadius: '8px',
                  fontWeight: 600,
                }}
              >
                PDF / Print
              </Button>
            </Box>

            <CustomTableScrollbar
              sx={{
                width: '100%',
                overflowX: 'auto',
              }}
            >
              <TableContainer>
                <Table
                  sx={{
                    minWidth: 900,

                    '& .MuiTableHead-root .MuiTableCell-root':
                      {
                        fontWeight:
                          'bold',

                        backgroundColor:
                          theme.palette
                            .primary
                            .main,

                        color:
                          '#fff',

                        fontSize:
                          '0.9rem',

                        padding:
                          '12px',

                        border: `1px solid ${theme.palette.divider}`,
                      },

                    '& .MuiTableBody-root .MuiTableCell-root':
                      {
                        border: `1px solid ${theme.palette.divider}`,

                        padding:
                          '8px 10px',

                        fontSize:
                          '0.875rem',
                      },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        S.No
                      </TableCell>

                      <TableCell>
                        Enquiry Code
                      </TableCell>

                      <TableCell>
                        Client Name
                      </TableCell>

                      <TableCell>
                        Mobile No
                      </TableCell>

                      <TableCell>
                        Enquiry Date
                      </TableCell>

                      <TableCell>
                        Next Follow-Up-Date
                      </TableCell>

                      <TableCell>
                        Product
                      </TableCell>

                      <TableCell>
                        Status
                      </TableCell>

                      <TableCell
                        sx={{
                          minWidth: 180,
                        }}
                      >
                        Assigned To
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor:
                            theme.palette
                              .primary
                              .dark,

                          minWidth: 80,
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedData.length >
                    0 ? (
                      paginatedData.map(
                        (
                          enq,
                          localIdx
                        ) => {
                          const rowNum =
                            (page -
                              1) *
                              rowsPerPage +
                            localIdx +
                            1;

                          const coursesDisplay =
                            enq.courses_details
                              ?.map(
                                (course) =>
                                  course.course_name ||
                                  course.name
                              )
                              .join(
                                ', '
                              ) ||
                            'N/A';

                          const statusDisplay =
                            enq.status_name ||
                            statusData.find(
                              (status) =>
                                String(
                                  status.id
                                ) ===
                                String(
                                  enq.status
                                )
                            )?.name ||
                            statusData.find(
                              (status) =>
                                String(
                                  status.id
                                ) ===
                                String(
                                  enq.status
                                )
                            )?.status_name ||
                            'N/A';

                          return (
                            <TableRow
                              key={
                                enq.id
                              }
                              sx={{
                                backgroundColor:
                                  localIdx %
                                    2 ===
                                  0
                                    ? theme
                                        .palette
                                        .background
                                        .paper
                                    : theme
                                        .palette
                                        .action
                                        .hover,

                                '&:hover':
                                  {
                                    backgroundColor:
                                      'rgba(0,0,0,0.04)',
                                  },
                              }}
                            >
                              <TableCell>
                                {
                                  rowNum
                                }
                              </TableCell>

                              <TableCell>
                                {enq.enquiry_code ||
                                  'N/A'}
                              </TableCell>

                              <TableCell>
                                {enq.candidate_name ||
                                  'N/A'}
                              </TableCell>

                              <TableCell>
                                {enq.mobile_no ||
                                  'N/A'}
                              </TableCell>

                              <TableCell>
                                {enq.enquiry_date ||
                                  'N/A'}
                              </TableCell>

                              <TableCell>
                                {enq.next_followup_date ||
                                  'N/A'}
                              </TableCell>

                              <TableCell>
                                {
                                  coursesDisplay
                                }
                              </TableCell>

                              <TableCell>
                                {
                                  statusDisplay
                                }
                              </TableCell>

                              {/* Assigned To */}

                              <TableCell>
                                <FormControl
                                  fullWidth
                                  size="small"
                                >
                                  <Select
                                    value={
                                      String(
                                        enq.assigned_to ||
                                          ''
                                      )
                                    }
                                    onChange={(
                                      e
                                    ) =>
                                      handleTableAssignedToChange(
                                        enq,
                                        e
                                          .target
                                          .value
                                      )
                                    }
                                    sx={{
                                      fontSize:
                                        '0.875rem',

                                      height: 36,
                                    }}
                                    displayEmpty
                                  >
                                    <MenuItem value="">
                                      <em>
                                        Select
                                      </em>
                                    </MenuItem>

                                    {assignedToData.map(
                                      (
                                        emp
                                      ) => (
                                        <MenuItem
                                          key={
                                            emp.id
                                          }
                                          value={String(
                                            emp.id
                                          )}
                                        >
                                          {getEmpName(
                                            emp
                                          )}
                                        </MenuItem>
                                      )
                                    )}
                                  </Select>
                                </FormControl>
                              </TableCell>

                              {/* Actions */}

                              <TableCell align="center">
                                <Tooltip title="Add / View Follow-Up">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleOpenFollowUpModal(
                                        enq
                                      )
                                    }
                                    sx={{
                                      color:
                                        theme
                                          .palette
                                          .primary
                                          .light,
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        }
                      )
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={
                            10
                          }
                          align="center"
                          sx={{
                            py: 4,
                            fontSize:
                              '1rem',
                          }}
                        >
                          No Data Found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CustomTableScrollbar>

            {/* Pagination */}

            {filteredEnquiries.length >
              0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent:
                    'space-between',
                  alignItems:
                    'center',
                  p: 2,
                  flexWrap:
                    'wrap',
                  gap: 1,
                }}
              >
                <Typography
                  variant="body2"
                  color={
                    theme.palette
                      .primary
                      .main
                  }
                >
                  Page{' '}
                  <strong>
                    {page}
                  </strong>{' '}
                  of{' '}
                  <strong>
                    {
                      totalPages
                    }
                  </strong>
                </Typography>

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  <IconButton
                    onClick={() =>
                      setPage(
                        (
                          current
                        ) =>
                          Math.max(
                            1,
                            current -
                              1
                          )
                      )
                    }
                    disabled={
                      page === 1
                    }
                    sx={{
                      color:
                        theme
                          .palette
                          .primary
                          .main,

                      '&.Mui-disabled':
                        {
                          opacity:
                            0.3,
                        },
                    }}
                  >
                    <ChevronLeft />
                  </IconButton>

                  {[
                    ...Array(
                      totalPages
                    ),
                  ].map(
                    (
                      _,
                      idx
                    ) => (
                      <IconButton
                        key={
                          idx +
                          1
                        }
                        onClick={() =>
                          setPage(
                            idx +
                              1
                          )
                        }
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius:
                            '50%',

                          backgroundColor:
                            page ===
                            idx +
                              1
                              ? theme
                                  .palette
                                  .primary
                                  .main
                              : 'transparent',

                          color:
                            page ===
                            idx +
                              1
                              ? theme
                                  .palette
                                  .primary
                                  .contrastText
                              : theme
                                  .palette
                                  .text
                                  .primary,

                          '&:hover':
                            {
                              backgroundColor:
                                page ===
                                idx +
                                  1
                                  ? theme
                                      .palette
                                      .primary
                                      .dark
                                  : theme
                                      .palette
                                      .action
                                      .hover,
                            },
                        }}
                      >
                        {
                          idx +
                          1
                        }
                      </IconButton>
                    )
                  )}

                  <IconButton
                    onClick={() =>
                      setPage(
                        (
                          current
                        ) =>
                          Math.min(
                            totalPages,
                            current +
                              1
                          )
                      )
                    }
                    disabled={
                      page >=
                      totalPages
                    }
                    sx={{
                      color:
                        theme
                          .palette
                          .primary
                          .main,

                      '&.Mui-disabled':
                        {
                          opacity:
                            0.3,
                        },
                    }}
                  >
                    <ChevronRight />
                  </IconButton>

                  <FormControl
                    size="small"
                    sx={{
                      minWidth: 100,
                    }}
                  >
                    <InputLabel>
                      Rows
                    </InputLabel>

                    <Select
                      value={
                        rowsPerPage
                      }
                      onChange={(
                        e
                      ) => {
                        setRowsPerPage(
                          parseInt(
                            e.target
                              .value,
                            10
                          )
                        );

                        setPage(
                          1
                        );
                      }}
                      label="Rows"
                      sx={{
                        height: 36,
                      }}
                    >
                      <MenuItem
                        value={
                          5
                        }
                      >
                        5
                      </MenuItem>

                      <MenuItem
                        value={
                          10
                        }
                      >
                        10
                      </MenuItem>

                      <MenuItem
                        value={
                          20
                        }
                      >
                        20
                      </MenuItem>

                      <MenuItem
                        value={
                          30
                        }
                      >
                        30
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Box>
            )}
          </Paper>
        )}

      {!loading &&
        isSearchPerformed &&
        enquiries.length === 0 &&
        !error && (
          <Typography
            variant="body1"
            sx={{
              mt: 2,
              px: 1,
            }}
          >
            No enquiries found for the selected criteria.
          </Typography>
        )}

      {/* ======================================================================
          FOLLOW-UP MODAL
      ====================================================================== */}

      <Modal
        open={
          showFollowUpModal
        }
        onClose={() =>
          setShowFollowUpModal(
            false
          )
        }
        BackdropProps={{
          style: {
            backgroundColor:
              'rgba(0,0,0,0.8)',
          },
        }}
      >
        <Box
          sx={{
            position:
              'absolute',

            top: '50%',
            left: '50%',

            transform:
              'translate(-50%, -50%)',

            width: {
              xs: '98%',
              sm: '90%',
              md: 900,
            },

            maxHeight: '92vh',

            bgcolor:
              'background.paper',

            borderRadius: 2,

            boxShadow: 24,

            overflow: 'hidden',

            display:
              'flex',

            flexDirection:
              'column',
          }}
        >
          {/* Modal Header */}

          <Box
            sx={{
              backgroundColor:
                theme.palette
                  .primary
                  .main,

              padding:
                '12px 16px',

              display: 'flex',

              justifyContent:
                'space-between',

              alignItems:
                'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight:
                  'bold',
              }}
            >
              Enquiry Follow-Up
            </Typography>

            <IconButton
              onClick={() =>
                setShowFollowUpModal(
                  false
                )
              }
              sx={{
                color:
                  '#fff',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Modal Body */}

          <CustomTableScrollbar
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2.5,
            }}
          >
            {modalLoading ? (
              <Box
                sx={{
                  display:
                    'flex',

                  justifyContent:
                    'center',

                  py: 6,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* ============================================================
                    PREFILLED ENQUIRY INFO
                ============================================================ */}

                {selectedEnquiry && (
                  <Box
                    sx={{
                      display:
                        'grid',

                      gridTemplateColumns:
                        {
                          xs: '1fr',
                          sm: '1fr 1fr',
                        },

                      gap: 2,

                      mb: 2.5,
                    }}
                  >
                    <TextField
                      label="Enquiry No."
                      value={
                        selectedEnquiry.enquiry_code ||
                        ''
                      }
                      InputProps={{
                        readOnly:
                          true,
                      }}
                      size="small"
                      sx={
                        fieldSx
                      }
                    />

                    <TextField
                      label="Enquiry Date."
                      value={
                        selectedEnquiry.enquiry_date ||
                        ''
                      }
                      InputProps={{
                        readOnly:
                          true,
                      }}
                      size="small"
                      sx={
                        fieldSx
                      }
                    />

                    <TextField
                      label="Client Name"
                      value={
                        selectedEnquiry.client_name ||
                        selectedEnquiry.candidate_name ||
                        ''
                      }
                      InputProps={{
                        readOnly:
                          true,
                      }}
                      size="small"
                      sx={
                        fieldSx
                      }
                    />

                    <TextField
                      label="Email-Id"
                      value={
                        selectedEnquiry.email ||
                        ''
                      }
                      InputProps={{
                        readOnly:
                          true,
                      }}
                      size="small"
                      sx={
                        fieldSx
                      }
                    />

                    <TextField
                      label="MobileNo"
                      value={
                        selectedEnquiry.mobile_no ||
                        ''
                      }
                      InputProps={{
                        readOnly:
                          true,
                      }}
                      size="small"
                      sx={
                        fieldSx
                      }
                    />

                    <TextField
                      label="Address"
                      value={
                        selectedEnquiry.address ||
                        ''
                      }
                      InputProps={{
                        readOnly:
                          true,
                      }}
                      size="small"
                      multiline
                      minRows={
                        1
                      }
                      sx={
                        fieldSx
                      }
                    />

                    {/* ✅ Automatically +10 days */}

                    <TextField
                      label="Next Follow-Up Date *"
                      type="date"
                      name="next_followup_date"
                      required
                      value={
                        followUpForm.next_followup_date
                      }
                      onChange={
                        handleFollowUpFormChange
                      }
                      InputLabelProps={{
                        shrink:
                          true,
                      }}
                      size="small"
                      sx={
                        fieldSx
                      }
                    />

                    <FormControl
                      size="small"
                      sx={
                        fieldSx
                      }
                    
                      required>
                      <InputLabel id="tracked-through-label" required>
                        Tracked Through
                      </InputLabel>

                      <Select
                        labelId="tracked-through-label"
                        name="followup_medium"
                        value={
                          followUpForm.followup_medium
                        }
                        onChange={
                          handleFollowUpFormChange
                        }
                        label="Tracked Through"
                      
                        required>
                        <MenuItem value="">
                          <em>
                            --Select--
                          </em>
                        </MenuItem>

                        {followUpMediumData.map(
                          (
                            medium
                          ) => (
                            <MenuItem
                              key={
                                medium.id
                              }
                              value={String(
                                medium.id
                              )}
                            >
                              {
                                medium.name
                              }
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>

                    <TextField
                      label="Preferred Time *"
                      type="time"
                      name="preferred_time"
                      required
                      value={
                        followUpForm.preferred_time
                      }
                      onChange={
                        handleFollowUpFormChange
                      }
                      InputLabelProps={{
                        shrink:
                          true,
                      }}
                      size="small"
                      sx={
                        fieldSx
                      }
                    />

                    <FormControl
                      size="small"
                      sx={
                        fieldSx
                      }
                    
                      required>
                      <InputLabel id="interest-level-label" required>
                        Interest Level
                      </InputLabel>

                      <Select
                        labelId="interest-level-label"
                        name="interest_level"
                        value={
                          followUpForm.interest_level
                        }
                        onChange={
                          handleFollowUpFormChange
                        }
                        label="Interest Level"
                      
                        required>
                        <MenuItem value="">
                          <em>
                            --Select--
                          </em>
                        </MenuItem>

                        {interestLevelData.map(
                          (
                            item
                          ) => (
                            <MenuItem
                              key={
                                item.id
                              }
                              value={String(
                                item.id
                              )}
                            >
                              {
                                item.level ||
                                  item.name
                              }
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>

                    <TextField
                      label="Reason For Decline/Delay *"
                      name="decline_reason"
                      required
                      value={
                        followUpForm.decline_reason
                      }
                      onChange={
                        handleFollowUpFormChange
                      }
                      size="small"
                      placeholder="Enter reason..."
                      sx={
                        fieldSx
                      }
                    />

                    <FormControl
                      size="small"
                      sx={
                        fieldSx
                      }
                    
                      required>
                      <InputLabel id="modal-assigned-to-label" required>
                        Assigned To
                      </InputLabel>

                      <Select
                        labelId="modal-assigned-to-label"
                        name="assigned_to"
                        value={
                          followUpForm.assigned_to
                        }
                        onChange={
                          handleFollowUpFormChange
                        }
                        label="Assigned To"
                      
                        required>
                        <MenuItem value="">
                          <em>
                            --Select--
                          </em>
                        </MenuItem>

                        {assignedToData.map(
                          (
                            employee
                          ) => (
                            <MenuItem
                              key={
                                employee.id
                              }
                              value={String(
                                employee.id
                              )}
                            >
                              {getEmpName(
                                employee
                              )}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>

                    <TextField
                      label="Remark *"
                      name="remark"
                      required
                      value={
                        followUpForm.remark
                      }
                      onChange={
                        handleFollowUpFormChange
                      }
                      size="small"
                      multiline
                      minRows={
                        2
                      }
                      sx={{
                        ...fieldSx,
                        gridColumn:
                          {
                            xs: '1',
                            sm: '1 / -1',
                          },
                      }}
                    />
                  </Box>
                )}

                {/* ============================================================
                    CHECKBOX ROW
                ============================================================ */}

                <Box
                  sx={{
                    display:
                      'flex',

                    flexWrap:
                      'wrap',

                    alignItems:
                      'center',

                    gap: {
                      xs: 1,
                      sm: 2,
                    },

                    mb: 2,

                    p: 1.5,

                    borderRadius:
                      1.5,

                    border: `1px solid ${theme.palette.divider}`,

                    backgroundColor:
                      theme.palette
                        .mode ===
                      'dark'
                        ? 'rgba(255,255,255,0.04)'
                        : 'rgba(0,0,0,0.02)',
                  }}
                >
                  {/* Demo Attended */}

                  <FormControlLabel
                    control={
                      <Checkbox
                        name="demo_attended"
                        checked={Boolean(
                          followUpForm.demo_attended
                        )}
                        onChange={
                          handleFollowUpFormChange
                        }
                        sx={{
                          color:
                            theme
                              .palette
                              .text
                              .secondary,

                          '&.Mui-checked':
                            {
                              color:
                                theme
                                  .palette
                                  .primary
                                  .main,
                            },
                        }}
                      />
                    }
                    label="Demo Attended"
                    sx={{
                      m: 0,

                      '& .MuiFormControlLabel-label':
                        {
                          color:
                            theme
                              .palette
                              .text
                              .primary,

                          fontSize:
                            '0.875rem',

                          fontWeight:
                            500,
                        },
                    }}
                  />

                  {/* Confirmed */}

                  <FormControlLabel
                    control={
                      <Checkbox
                        name="confirmed_for_joining"
                        checked={Boolean(
                          followUpForm.confirmed_for_joining
                        )}
                        onChange={
                          handleFollowUpFormChange
                        }
                        sx={{
                          color:
                            theme
                              .palette
                              .text
                              .secondary,

                          '&.Mui-checked':
                            {
                              color:
                                theme
                                  .palette
                                  .primary
                                  .main,
                            },
                        }}
                      />
                    }
                    label="Confirmed for Joining"
                    sx={{
                      m: 0,

                      '& .MuiFormControlLabel-label':
                        {
                          color:
                            theme
                              .palette
                              .text
                              .primary,

                          fontSize:
                            '0.875rem',

                          fontWeight:
                            500,
                        },
                    }}
                  />

                  {/* Schedule Demo */}

                  <FormControlLabel
                    control={
                      <Checkbox
                        name="schedule_demo_class"
                        checked={Boolean(
                          followUpForm.schedule_demo_class
                        )}
                        onChange={
                          handleFollowUpFormChange
                        }
                        sx={{
                          color:
                            theme
                              .palette
                              .text
                              .secondary,

                          '&.Mui-checked':
                            {
                              color:
                                theme
                                  .palette
                                  .primary
                                  .main,
                            },
                        }}
                      />
                    }
                    label="Schedule Demo Class"
                    sx={{
                      m: 0,

                      '& .MuiFormControlLabel-label':
                        {
                          color:
                            theme
                              .palette
                              .text
                              .primary,

                          fontSize:
                            '0.875rem',

                          fontWeight:
                            500,
                        },
                    }}
                  />
                </Box>

                {/* ============================================================
                    DEMO ATTENDED DATE
                ============================================================ */}

                {followUpForm.demo_attended && (
                  <Box
                    sx={{
                      mb: 2,
                      maxWidth: 300,
                    }}
                  >
                    <TextField
                      label="Demo Date *"
                      type="date"
                      name="demo_date"
                      required={Boolean(followUpForm.demo_attended)}
                      value={
                        followUpForm.demo_date
                      }
                      onChange={
                        handleFollowUpFormChange
                      }
                      InputLabelProps={{
                        shrink:
                          true,
                      }}
                      size="small"
                      fullWidth
                      sx={
                        fieldSx
                      }
                    />
                  </Box>
                )}

                {/* ============================================================
                    SCHEDULE DEMO SUMMARY
                ============================================================ */}

                {followUpForm.schedule_demo_class && (
                  <Box
                    sx={{
                      mb: 2,

                      p: 2,

                      borderRadius: 2,

                      backgroundColor:
                        theme.palette
                          .mode ===
                        'dark'
                          ? 'rgba(25,118,210,0.1)'
                          : 'rgba(25,118,210,0.05)',

                      border: `1px solid ${theme.palette.primary.main}`,
                    }}
                  >
                    <Box
                      sx={{
                        display:
                          'flex',

                        justifyContent:
                          'space-between',

                        alignItems:
                          'center',

                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight:
                            'bold',

                          color:
                            theme
                              .palette
                              .primary
                              .main,
                        }}
                      >
                        Demo Class Details
                      </Typography>

                      <Button
                        size="small"
                        onClick={() => {
                          const course =
                            followUpForm.demo_course ||
                            '';

                          const batch =
                            followUpForm.demo_batch ||
                            '';

                          const faculty =
                            followUpForm.demo_faculty ||
                            '';

                          setTempDemoCourse(
                            course
                          );

                          setTempDemoBatch(
                            batch
                          );

                          setTempDemoFaculty(
                            faculty
                          );

                          setTempDemoDate(
                            followUpForm.demo_date ||
                              getDateAfterDays(
                                0
                              )
                          );

                          setTempDemoTime(
                            followUpForm.demo_time ||
                              ''
                          );

                          setShowDemoModal(
                            true
                          );

                          if (course) {
                            fetchDemoBatches(
                              course,
                              batch,
                              faculty
                            );
                          }
                        }}
                        sx={{
                          textTransform:
                            'none',
                        }}
                      >
                        Edit Demo
                      </Button>
                    </Box>

                    <Box
                      sx={{
                        display:
                          'grid',

                        gridTemplateColumns:
                          'repeat(auto-fill, minmax(150px, 1fr))',

                        gap: 1,
                      }}
                    >
                      {[
                        {
                          label:
                            'Course',

                          value:
                            getCourseName(
                              followUpForm.demo_course,
                              courseData
                            ),
                        },

                        {
                          label:
                            'Batch',

                          value:
                            demoBatches.find(
                              (
                                batch
                              ) =>
                                String(
                                  batch.id
                                ) ===
                                String(
                                  followUpForm.demo_batch
                                )
                            )?.batch_name ||
                            demoBatches.find(
                              (
                                batch
                              ) =>
                                String(
                                  batch.id
                                ) ===
                                String(
                                  followUpForm.demo_batch
                                )
                            )?.name ||
                            demoBatches.find(
                              (
                                batch
                              ) =>
                                String(
                                  batch.id
                                ) ===
                                String(
                                  followUpForm.demo_batch
                                )
                            )?.batch_code ||
                            'Not selected',
                        },

                        {
                          label:
                            'Faculty',

                          value:
                            demoFaculty.find(
                              (
                                faculty
                              ) =>
                                String(
                                  faculty.id
                                ) ===
                                String(
                                  followUpForm.demo_faculty
                                )
                            )?.name ||
                            (
                              followUpForm.demo_faculty
                                ? `Trainer ${followUpForm.demo_faculty}`
                                : 'Not selected'
                            ),
                        },

                        {
                          label:
                            'Date',

                          value:
                            followUpForm.demo_date ||
                            'Not selected',
                        },

                        {
                          label:
                            'Time',

                          value:
                            followUpForm.demo_time ||
                            'Not selected',
                        },
                      ].map(
                        (
                          item
                        ) => (
                          <Box
                            key={
                              item.label
                            }
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {
                                item.label
                              }
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight:
                                  'medium',
                              }}
                            >
                              {
                                item.value
                              }
                            </Typography>
                          </Box>
                        )
                      )}
                    </Box>
                  </Box>
                )}

                {/* ============================================================
                    ACTION BUTTONS
                ============================================================ */}

                <Box
                  sx={{
                    display:
                      'flex',

                    justifyContent:
                      'center',

                    gap: 2,

                    mb: 3,
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={
                      handleSaveFollowUp
                    }
                    disabled={
                      savingFollowUp
                    }
                    sx={{
                      fontWeight:
                        'bold',

                      px: 5,

                      minWidth:
                        120,

                      backgroundColor:
                        '#4caf50',

                      '&:hover': {
                        backgroundColor:
                          '#388e3c',
                      },
                    }}
                  >
                    {savingFollowUp ? (
                      <CircularProgress
                        size={
                          22
                        }
                        color="inherit"
                      />
                    ) : (
                      'SAVE'
                    )}
                  </Button>

                  <Button
                    variant="contained"
                    onClick={() =>
                      setFollowUpForm({
                        ...defaultFollowUpForm,
                      })
                    }
                    sx={{
                      fontWeight:
                        'bold',

                      px: 5,

                      minWidth:
                        120,

                      backgroundColor:
                        '#2196f3',

                      '&:hover': {
                        backgroundColor:
                          '#1565c0',
                      },
                    }}
                  >
                    RESET
                  </Button>
                </Box>

                {/* ============================================================
                    FOLLOW-UP HISTORY
                ============================================================ */}

                <Box
                  sx={{
                    backgroundColor:
                      theme.palette
                        .primary
                        .main,

                    p: 1,

                    borderRadius:
                      '4px 4px 0 0',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color:
                        '#fff',

                      fontWeight:
                        'bold',

                      px: 1,
                    }}
                  >
                    Follow-Up History
                  </Typography>
                </Box>

                <CustomTableScrollbar
                  sx={{
                    width: '100%',
                    overflowX:
                      'auto',
                  }}
                >
                  <Table
                    size="small"
                    sx={{
                      '& .MuiTableHead-root .MuiTableCell-root':
                        {
                          backgroundColor:
                            theme
                              .palette
                              .primary
                              .light,

                          color:
                            theme
                              .palette
                              .primary
                              .contrastText,

                          fontWeight:
                            'bold',

                          fontSize:
                            '0.8rem',

                          border: `1px solid ${theme.palette.divider}`,

                          padding:
                            '8px',
                        },

                      '& .MuiTableBody-root .MuiTableCell-root':
                        {
                          border: `1px solid ${theme.palette.divider}`,

                          padding:
                            '6px 8px',

                          fontSize:
                            '0.8rem',
                        },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          #
                        </TableCell>

                        <TableCell>
                          Enquiry Code
                        </TableCell>

                        <TableCell>
                          Client Name
                        </TableCell>

                        <TableCell>
                          Product Name
                        </TableCell>

                        <TableCell>
                          Follow-Up-Date
                        </TableCell>

                        <TableCell>
                          Next Follow-Up-Date
                        </TableCell>

                        <TableCell>
                          Updated By
                        </TableCell>

                        <TableCell>
                          Status
                        </TableCell>

                        <TableCell>
                          Remark
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {followUpHistory.length >
                      0 ? (
                        followUpHistory.map(
                          (
                            record,
                            index
                          ) => (
                            <TableRow
                              key={
                                record.id ||
                                index
                              }
                            >
                              <TableCell>
                                {
                                  index +
                                  1
                                }
                              </TableCell>

                              <TableCell>
                                {record.enquiry_code ||
                                  selectedEnquiry?.enquiry_code ||
                                  'N/A'}
                              </TableCell>

                              <TableCell>
                                {record.client_name ||
                                  selectedEnquiry?.client_name ||
                                  'N/A'}
                              </TableCell>

                              <TableCell>
                                {record.demo_course_name ||
                                  'N/A'}
                              </TableCell>

                              <TableCell>
                                {record.followup_date ||
                                  'N/A'}
                              </TableCell>

                              <TableCell>
                                {record.next_followup_date ||
                                  'N/A'}
                              </TableCell>

                              <TableCell>
                                {record.updated_by
                                  ? String(
                                      record.updated_by
                                    )
                                  : record.created_by
                                  ? String(
                                      record.created_by
                                    )
                                  : 'N/A'}
                              </TableCell>

                              <TableCell>
                                {record.status_name ||
                                  'N/A'}
                              </TableCell>

                         <TableCell>
  <Button
    variant="outlined"
    size="small"
    onClick={() => {
      setSelectedRemark(record.remark || 'N/A');
      setRemarkDialogOpen(true);
    }}
  >
    View
  </Button>
</TableCell>
                            </TableRow>
                          )
                        )
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={
                              9
                            }
                            align="center"
                            sx={{
                              py: 3,

                              color:
                                theme
                                  .palette
                                  .text
                                  .secondary,
                            }}
                          >
                            No follow-up history found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CustomTableScrollbar>
              </>
            )}
          </CustomTableScrollbar>
          <Dialog
  open={remarkDialogOpen}
  onClose={() => setRemarkDialogOpen(false)}
  maxWidth="sm"
  fullWidth
>
  <DialogTitle>
    Remark
  </DialogTitle>

  <DialogContent dividers>
    <Typography
      variant="body2"
      sx={{
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {selectedRemark || 'N/A'}
    </Typography>
  </DialogContent>

  <DialogActions>
    <Button
      onClick={() => setRemarkDialogOpen(false)}
      variant="contained"
    >
      Close
    </Button>
  </DialogActions>
</Dialog>
        </Box>
      </Modal>

      {/* ======================================================================
          SCHEDULE DEMO CLASS SUB-MODAL
      ====================================================================== */}

      <Modal
        open={
          showDemoModal
        }
        onClose={() =>
          setShowDemoModal(
            false
          )
        }
        BackdropProps={{
          style: {
            backgroundColor:
              'rgba(0,0,0,0.85)',
          },
        }}
        sx={{
          zIndex: 1400,
        }}
      >
        <Box
          sx={{
            position:
              'absolute',

            top: '50%',

            left: '50%',

            transform:
              'translate(-50%, -50%)',

            width: {
              xs: '95%',
              sm: 700,
            },

            maxHeight:
              '85vh',

            bgcolor:
              'background.paper',

            borderRadius: 2,

            boxShadow: 24,

            overflow: 'hidden',

            display:
              'flex',

            flexDirection:
              'column',
          }}
        >
          {/* Header */}

          <Box
            sx={{
              backgroundColor:
                theme.palette
                  .primary
                  .main,

              padding:
                '12px 20px',

              display:
                'flex',

              justifyContent:
                'space-between',

              alignItems:
                'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight:
                  'bold',
              }}
            >
              Schedule Demo Class
            </Typography>

            <IconButton
              onClick={() =>
                setShowDemoModal(
                  false
                )
              }
              sx={{
                color:
                  '#fff',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}

          <CustomTableScrollbar
            sx={{
              flex: 1,

              overflowY:
                'auto',

              p: 2.5,
            }}
          >
            <CustomTableScrollbar
              sx={{
                width: '100%',
                overflowX:
                  'auto',
              }}
            >
              <Table
                size="small"
                sx={{
                  '& .MuiTableHead-root .MuiTableCell-root':
                    {
                      backgroundColor:
                        theme
                          .palette
                          .primary
                          .light,

                      color:
                        theme
                          .palette
                          .primary
                          .contrastText,

                      fontWeight:
                        'bold',

                      border: `1px solid ${theme.palette.divider}`,

                      padding:
                        '8px',
                    },

                  '& .MuiTableBody-root .MuiTableCell-root':
                    {
                      border: `1px solid ${theme.palette.divider}`,

                      padding:
                        '8px',
                    },

                  minWidth:
                    700,
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        width: 32,
                      }}
                    />

                    <TableCell>
                      Course
                    </TableCell>

                    <TableCell>
                      Batch
                    </TableCell>

                    <TableCell>
                      Faculty
                    </TableCell>

                    <TableCell>
                      Date
                    </TableCell>

                    <TableCell>
                      Time
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow>
                    <TableCell>
                      ✏️
                    </TableCell>

                    {/* ========================================================
                        COURSE
                        
                        Global Branch → Course
                    ======================================================== */}

                    <TableCell>
                      <FormControl
                        size="small"
                        fullWidth
                        sx={
                          fieldSx
                        }
                        disabled={
                          !selectedBranchId
                        }
                      >
                        <Select
                          value={
                            tempDemoCourse
                          }
                          onChange={(
                            e
                          ) =>
                            handleDemoCourseChange(
                              e
                                .target
                                .value
                            )
                          }
                          displayEmpty
                          MenuProps={{
                            sx: {
                              zIndex: 1600,
                            },

                            PaperProps:
                              {
                                sx: {
                                  maxHeight:
                                    250,
                                },
                              },
                          }}
                        >
                          <MenuItem value="">
                            <em>
                              {selectedBranchId
                                ? '--Select--'
                                : 'Select Global Branch First'}
                            </em>
                          </MenuItem>

                          {courseData.map(
                            (
                              course
                            ) => (
                              <MenuItem
                                key={
                                  course.id
                                }
                                value={String(
                                  course.id
                                )}
                              >
                                {course.name ||
                                  course.course_name ||
                                  course.title}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>
                    </TableCell>

                    {/* ========================================================
                        BATCH
                        
                        Selected Course + Global Branch → Batch
                    ======================================================== */}

                    <TableCell>
                      <FormControl
                        size="small"
                        fullWidth
                        sx={
                          fieldSx
                        }
                        disabled={
                          !tempDemoCourse ||
                          loadingDemoBatches
                        }
                      >
                        <Select
                          value={
                            tempDemoBatch
                          }
                          onChange={(
                            e
                          ) =>
                            handleDemoBatchChange(
                              e
                                .target
                                .value
                            )
                          }
                          displayEmpty
                          MenuProps={{
                            sx: {
                              zIndex: 1600,
                            },

                            PaperProps:
                              {
                                sx: {
                                  maxHeight:
                                    250,
                                },
                              },
                          }}
                        >
                          <MenuItem value="">
                            {loadingDemoBatches ? (
                              'Loading...'
                            ) : (
                              <em>
                                --Select--
                              </em>
                            )}
                          </MenuItem>

                          {demoBatches.map(
                            (
                              batch
                            ) => (
                              <MenuItem
                                key={
                                  batch.id
                                }
                                value={String(
                                  batch.id
                                )}
                              >
                                {batch.batch_name ||
                                  batch.name ||
                                  batch.batch_code ||
                                  `Batch ${batch.id}`}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>
                    </TableCell>

                    {/* ========================================================
                        FACULTY
                        
                        Selected Batch → Faculty/Trainer
                        
                        This is fixed automatically from batch.
                    ======================================================== */}

                    <TableCell>
                      <FormControl
                        size="small"
                        fullWidth
                        sx={
                          fieldSx
                        }
                        disabled={
                          !tempDemoBatch ||
                          loadingDemoFaculty
                        }
                      >
                        <Select
                          value={
                            tempDemoFaculty
                          }
                          onChange={(
                            e
                          ) =>
                            setTempDemoFaculty(
                              e
                                .target
                                .value
                            )
                          }
                          displayEmpty
                          MenuProps={{
                            sx: {
                              zIndex: 1600,
                            },

                            PaperProps:
                              {
                                sx: {
                                  maxHeight:
                                    250,
                                },
                              },
                          }}
                        >
                          <MenuItem value="">
                            {loadingDemoFaculty ? (
                              'Loading...'
                            ) : (
                              <em>
                                --Select--
                              </em>
                            )}
                          </MenuItem>

                          {demoFaculty.map(
                            (
                              faculty
                            ) => (
                              <MenuItem
                                key={
                                  faculty.id
                                }
                                value={String(
                                  faculty.id
                                )}
                              >
                                {
                                  faculty.name
                                }
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>
                    </TableCell>

                    {/* ========================================================
                        DATE
                    ======================================================== */}

                    <TableCell>
                      <TextField
                        type="date"
                        value={
                          tempDemoDate
                        }
                        onChange={(
                          e
                        ) =>
                          setTempDemoDate(
                            e
                              .target
                              .value
                          )
                        }
                        InputLabelProps={{
                          shrink:
                            true,
                        }}
                        size="small"
                        sx={
                          fieldSx
                        }
                      />
                    </TableCell>

                    {/* ========================================================
                        TIME
                    ======================================================== */}

                    <TableCell>
                      <TextField
                        type="time"
                        value={
                          tempDemoTime
                        }
                        onChange={(
                          e
                        ) =>
                          setTempDemoTime(
                            e
                              .target
                              .value
                          )
                        }
                        InputLabelProps={{
                          shrink:
                            true,
                        }}
                        size="small"
                        sx={
                          fieldSx
                        }
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CustomTableScrollbar>
          </CustomTableScrollbar>

          {/* Footer */}

          <Box
            sx={{
              display:
                'flex',

              justifyContent:
                'flex-end',

              gap: 2,

              p: 2,

              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setShowDemoModal(
                  false
                );

                if (
                  !followUpForm.demo_course
                ) {
                  setFollowUpForm(
                    (
                      previous
                    ) => ({
                      ...previous,

                      schedule_demo_class:
                        false,
                    })
                  );
                }
              }}
             data-app-cancel="true">
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={
                handleSaveDemoDetails
              }
              disabled={
                !tempDemoCourse ||
                !tempDemoBatch ||
                !tempDemoFaculty ||
                !tempDemoDate ||
                !tempDemoTime
              }
              sx={{
                backgroundColor:
                  theme.palette
                    .primary
                    .main,

                '&:hover': {
                  backgroundColor:
                    theme.palette
                      .primary
                      .dark,
                },
              }}
            >
              Apply Demo Details
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ======================================================================
          USER GUIDE MODAL
      ====================================================================== */}

      <Modal
        open={
          showUserGuideModal
        }
        onClose={() =>
          setShowUserGuideModal(
            false
          )
        }
        BackdropProps={{
          style: {
            backgroundColor:
              'rgba(0,0,0,0.8)',
          },
        }}
      >
        <Box
          sx={{
            position:
              'absolute',

            top: '50%',

            left: '50%',

            transform:
              'translate(-50%, -50%)',

            width: {
              xs: '95%',
              sm: 700,
              md: 860,
            },

            maxHeight:
              '85vh',

            bgcolor:
              'background.paper',

            borderRadius: 2,

            boxShadow: 24,

            overflow: 'hidden',

            display:
              'flex',

            flexDirection:
              'column',
          }}
        >
          {/* Header */}

          <Box
            sx={{
              background:
                `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,

              padding:
                '14px 20px',

              display:
                'flex',

              justifyContent:
                'space-between',

              alignItems:
                'center',
            }}
          >
            <Box
              sx={{
                display:
                  'flex',

                alignItems:
                  'center',

                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  backgroundColor:
                    'rgba(255,255,255,0.15)',

                  borderRadius:
                    '50%',

                  width: 36,

                  height: 36,

                  display:
                    'flex',

                  alignItems:
                    'center',

                  justifyContent:
                    'center',
                }}
              >
                <InfoOutlineIcon
                  sx={{
                    color:
                      '#fff',

                    fontSize:
                      20,
                  }}
                />
              </Box>

              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    color:
                      '#fff',

                    fontWeight:
                      700,

                    lineHeight:
                      1.2,
                  }}
                >
                  Enquiry Follow-Ups —
                  Guide
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color:
                      'rgba(255,255,255,0.65)',
                  }}
                >
                  Complete reference for all features and fields
                </Typography>
              </Box>
            </Box>

            <IconButton
              onClick={() =>
                setShowUserGuideModal(
                  false
                )
              }
              sx={{
                color:
                  '#fff',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <CustomTableScrollbar
            sx={{
              overflowY:
                'auto',

              p: 2.5,
            }}
          >
            {/* Intro */}

            <Box
              sx={{
                backgroundColor:
                  '#f0f4ff',

                border:
                  '1px solid #c5d3f0',

                borderRadius:
                  2,

                p: 2,

                mb: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color:
                    '#1a237e',

                  lineHeight:
                    1.7,
                }}
              >
                The{' '}
                <strong>
                  Enquiry
                  Follow-Ups
                </strong>{' '}
                page lets staff track and update follow-up actions on active enquiries.
                Filter enquiries by type, click the eye icon to open the follow-up modal,
                fill in details, and save.
              </Typography>
            </Box>

            {[
              {
                icon: (
                  <FilterListIcon
                    sx={{
                      color:
                        '#1976d2',

                      fontSize:
                        22,
                    }}
                  />
                ),

                title:
                  'Follow Up Type Filter',

                color:
                  '#e3f2fd',

                borderColor:
                  '#1976d2',

                items: [
                  {
                    label:
                      'Selected Date Wise',

                    desc:
                      'Filter by custom date range (From → To). Click Search after selecting dates.',
                  },

                  {
                    label:
                      'Current Date',

                    desc:
                      'Loads all enquiries with next follow-up date = today.',
                  },

                  {
                    label:
                      'Pending',

                    desc:
                      'Lists all enquiries whose next follow-up date is before today.',
                  },
                ],
              },

              {
                icon: (
                  <AssignmentIcon
                    sx={{
                      color:
                        '#7b1fa2',

                      fontSize:
                        22,
                    }}
                  />
                ),

                title:
                  'Follow-Up Modal Fields',

                color:
                  '#f3e5f5',

                borderColor:
                  '#7b1fa2',

                items: [
                  {
                    label:
                      'Next Follow-Up Date',

                    desc:
                      'Defaults to 10 days from today and can be changed manually.',
                  },

                  {
                    label:
                      'Tracked Through',

                    desc:
                      'Medium used to track the follow-up (Phone, Email, etc.).',
                  },

                  {
                    label:
                      'Interest Level',

                    desc:
                      'Rate how interested the candidate is.',
                  },

                  {
                    label:
                      'Reason For Decline/Delay',

                    desc:
                      'If applicable, enter the reason.',
                  },

                  {
                    label:
                      'Assigned To',

                    desc:
                      'Counsellor handling this enquiry.',
                  },

                  {
                    label:
                      'Remark',

                    desc:
                      'Notes about the follow-up interaction.',
                  },
                ],
              },

              {
                icon: (
                  <CheckCircleOutlineIcon
                    sx={{
                      color:
                        '#2e7d32',

                      fontSize:
                        22,
                    }}
                  />
                ),

                title:
                  'Checkboxes',

                color:
                  '#e8f5e9',

                borderColor:
                  '#2e7d32',

                items: [
                  {
                    label:
                      'Demo Attended',

                    desc:
                      'Check if the candidate attended a demo. A Demo Date field will appear.',
                  },

                  {
                    label:
                      'Confirmed for Joining',

                    desc:
                      'Mark if the candidate has confirmed joining.',
                  },

                  {
                    label:
                      'Schedule Demo Class',

                    desc:
                      'Opens a sub-modal to schedule a demo class with Course, Batch, Faculty, Date, and Time.',
                  },
                ],
              },

              {
                icon: (
                  <SaveIcon
                    sx={{
                      color:
                        '#0277bd',

                      fontSize:
                        22,
                    }}
                  />
                ),

                title:
                  'Other Actions',

                color:
                  '#e1f5fe',

                borderColor:
                  '#0277bd',

                items: [
                  {
                    label:
                      'Assigned To (Table)',

                    desc:
                      'Directly update the counsellor assigned to an enquiry from the main table.',
                  },

                  {
                    label:
                      'Eye Icon',

                    desc:
                      'Opens the follow-up modal for that enquiry, with history shown below.',
                  },

                  {
                    label:
                      'Reset (in modal)',

                    desc:
                      'Resets all follow-up form fields. Next Follow-Up Date returns to 10 days from today.',
                  },

                  {
                    label:
                      'Search Bar',

                    desc:
                      'Instantly filters the table by Enquiry Code, Client Name, or Mobile No.',
                  },
                ],
              },
            ].map(
              (
                section,
                sectionIndex
              ) => (
                <Box
                  key={
                    sectionIndex
                  }
                  sx={{
                    backgroundColor:
                      section.color,

                    border: `1px solid ${section.borderColor}30`,

                    borderLeft: `4px solid ${section.borderColor}`,

                    borderRadius:
                      2,

                    p: 2,

                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      display:
                        'flex',

                      alignItems:
                        'center',

                      gap: 1,

                      mb: 1.5,
                    }}
                  >
                    {
                      section.icon
                    }

                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight:
                          700,

                        color:
                          '#1a1a2e',

                        fontSize:
                          '0.95rem',
                      }}
                    >
                      {
                        section.title
                      }
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display:
                        'flex',

                      flexDirection:
                        'column',

                      gap: 0.8,
                    }}
                  >
                    {section.items.map(
                      (
                        item,
                        itemIndex
                      ) => (
                        <Box
                          key={
                            itemIndex
                          }
                          sx={{
                            display:
                              'flex',

                            gap: 1,

                            alignItems:
                              'flex-start',
                          }}
                        >
                          <Chip
                            label={
                              item.label
                            }
                            size="small"
                            sx={{
                              fontSize:
                                '0.7rem',

                              fontWeight:
                                600,

                              height:
                                22,

                              flexShrink:
                                0,

                              backgroundColor:
                                `${section.borderColor}18`,

                              color:
                                section.borderColor,

                              border: `1px solid ${section.borderColor}40`,

                              mt: '1px',
                            }}
                          />

                          <Typography
                            variant="body2"
                            sx={{
                              color:
                                '#424242',

                              lineHeight:
                                1.5,

                              fontSize:
                                '0.82rem',
                            }}
                          >
                            {
                              item.desc
                            }
                          </Typography>
                        </Box>
                      )
                    )}
                  </Box>
                </Box>
              )
            )}
          </CustomTableScrollbar>

          <Box
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,

              px: 2.5,

              py: 1.5,

              display:
                'flex',

              justifyContent:
                'flex-end',

              backgroundColor:
                theme.palette
                  .background
                  .default,
            }}
          >
            <Button
              onClick={() =>
                setShowUserGuideModal(
                  false
                )
              }
              variant="contained"
              sx={{
                fontWeight:
                  600,

                px: 3,

                backgroundColor:
                  theme.palette
                    .primary
                    .main,

                '&:hover': {
                  backgroundColor:
                    theme.palette
                      .primary
                      .dark,
                },
              }}
            >
              Got It
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ======================================================================
          SNACKBAR
      ====================================================================== */}

      <Snackbar
        open={
          openSnackbar
        }
        autoHideDuration={
          4000
        }
        onClose={() =>
          setOpenSnackbar(
            false
          )
        }
        anchorOrigin={{
          vertical:
            'top',
          horizontal:
            'center',
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(
              false
            )
          }
          severity={
            snackbarSeverity
          }
          sx={{
            width: '100%',
          }}
        >
          {
            snackbarMessage
          }
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EnquiryFollowUp;